import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Alert,
  Spinner
} from 'reactstrap';
import Select from 'react-select';
import { createModuleLogger } from '../../utils/logger';
import { updateTrabajoInterno, validateTrabajoInterno } from '../../services/TipoTrabajoService';
import './TipoTrabajoModal.css';

// Crear logger para este componente
const modalLogger = createModuleLogger('TipoTrabajoModal');

interface TipoTrabajoModalProps {
  isOpen: boolean;
  toggle: () => void;
  ordenId: string;
  currentValue: boolean | null;
  onSave: (ordenId: string, trabajoInterno: boolean) => Promise<void>;
}

const TipoTrabajoModal: React.FC<TipoTrabajoModalProps> = ({
  isOpen,
  toggle,
  ordenId,
  currentValue,
  onSave
}) => {
  // Estados del componente
  const [selectedValue, setSelectedValue] = useState<{ value: boolean; label: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');

  // Opciones para el select
  const tipoTrabajoOptions = [
    { value: true, label: 'INTERNO' },
    { value: false, label: 'EXTERNO' }
  ];

  // Inicializar valor seleccionado cuando cambia el currentValue
  useEffect(() => {
    if (currentValue !== null) {
      const option = tipoTrabajoOptions.find(opt => opt.value === currentValue);
      setSelectedValue(option || null);
    } else {
      setSelectedValue(null);
    }
  }, [currentValue]);

  // Limpiar estados cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setError('');
      setValidationError('');
      setSaving(false);
    }
  }, [isOpen]);

  const handleSelectChange = (option: { value: boolean; label: string } | null) => {
    setSelectedValue(option);
    setValidationError(''); // Limpiar error de validación al cambiar selección
  };

  const handleSave = async () => {
    // Validar que se haya seleccionado una opción
    if (!selectedValue) {
      setValidationError('Por favor seleccione un tipo de trabajo');
      return;
    }

    // Validar el valor seleccionado
    if (!validateTrabajoInterno(selectedValue.value)) {
      setValidationError('Trabajo interno inválido');
      return;
    }

    setSaving(true);
    setError('');
    setValidationError('');

    try {
      // Usar el servicio para actualizar
      const result = await updateTrabajoInterno(ordenId, selectedValue.value);
      
      if (result.success) {
        modalLogger.info('Trabajo interno actualizado exitosamente', { 
          ordenId, 
          trabajoInterno: selectedValue.value,
          updatedData: result.data
        });

        // Llamar al callback de éxito si existe
        if (onSave) {
          await onSave(ordenId, selectedValue.value);
        }

        // Cerrar modal después del guardado exitoso
        toggle();
      } else {
        // Manejar error del servicio
        setError(result.error || 'Error al guardar los cambios');
        modalLogger.error('Error del servicio al guardar trabajo interno', { 
          error: result.error, 
          ordenId, 
          trabajoInterno: selectedValue.value 
        });
      }

    } catch (err) {
      const errorMessage = 'Error inesperado al guardar los cambios';
      setError(errorMessage);
      modalLogger.error('Error inesperado al guardar trabajo interno', { 
        error: err, 
        ordenId, 
        trabajoInterno: selectedValue.value 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setError('');
    setValidationError('');
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} size="md" className="tipo-trabajo-modal">
      <ModalHeader toggle={handleCancel}>
        Editar Tipo de Trabajo
      </ModalHeader>
      
      <ModalBody>
        {error && (
          <Alert color="danger">
            {error}
          </Alert>
        )}

        {validationError && (
          <Alert color="warning">
            {validationError}
          </Alert>
        )}

        <FormGroup>
          <Label for="tipo-trabajo-select">
            Seleccione el tipo de trabajo:
          </Label>
          <Select
            id="tipo-trabajo-select"
            value={selectedValue}
            onChange={handleSelectChange}
            options={tipoTrabajoOptions}
            placeholder="Seleccione una opción..."
            isClearable={false}
            isSearchable={false}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: '38px',
                borderColor: validationError ? '#dc3545' : provided.borderColor,
                '&:hover': {
                  borderColor: validationError ? '#dc3545' : provided.borderColor,
                }
              })
            }}
          />
        </FormGroup>

        <div className="tipo-info">
          <small>
            <strong>INTERNO:</strong> Trabajo realizado internamente por la empresa<br/>
            <strong>EXTERNO:</strong> Trabajo subcontratado a terceros
          </small>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button 
          color="secondary" 
          onClick={handleCancel} 
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleSave} 
          disabled={saving || !selectedValue}
        >
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TipoTrabajoModal;
